import posthog from 'posthog-js';
import { POSTHOG_HOST, POSTHOG_KEY, isDev } from '../variables';

export const isPostHogEnabled = (): boolean => Boolean(POSTHOG_KEY && !isDev);

const isTrue = (value: string | undefined) => value === 'true';

const shouldDisableSessionRecording = isTrue(process.env.REACT_APP_POSTHOG_DISABLE_SESSION_RECORDING);
const shouldDisableSurveys = isTrue(process.env.REACT_APP_POSTHOG_DISABLE_SURVEYS);
const shouldDisableHeatmaps = isTrue(process.env.REACT_APP_POSTHOG_DISABLE_HEATMAPS);

export const initPostHog = (): void => {
    if (!POSTHOG_KEY || !isPostHogEnabled()) {
        return;
    }

    posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        defaults: '2026-01-30',

        // Core collection needed for Heatmaps and replay-backed UX debugging.
        autocapture: true,
        capture_pageview: 'history_change',
        capture_pageleave: 'if_capture_pageview',
        capture_dead_clicks: true,

        // PostHog products requested.
        capture_exceptions: true,
        disable_session_recording: shouldDisableSessionRecording,
        enable_heatmaps: !shouldDisableHeatmaps,
        disable_surveys: shouldDisableSurveys,
        disable_surveys_automatic_display: false,

        // Needed for feature flags / early access cohorting.
        person_profiles: 'identified_only',
        loaded: (instance: {
            register_once: (properties: Record<string, unknown>) => void;
            reloadFeatureFlags: () => void;
            startSessionRecording: () => void;
        }) => {
            instance.register_once({
                app_name: 'root',
                app_platform: 'web',
            });

            // Force a flags refresh once the client is ready.
            instance.reloadFeatureFlags();

            if (!shouldDisableSessionRecording) {
                instance.startSessionRecording();
            }
        },
    });
};

export const identifyPostHogUser = <T extends Record<string, unknown>>(
    distinctId: string,
    properties?: T
): void => {
    if (!isPostHogEnabled() || !distinctId) {
        return;
    }

    posthog.identify(distinctId, properties);
    if (properties) {
        posthog.setPersonPropertiesForFlags(properties, true);
    }
};

export const setPostHogCompanyGroup = <T extends Record<string, unknown>>(
    companyId: string,
    properties?: T
): void => {
    if (!isPostHogEnabled() || !companyId) {
        return;
    }

    posthog.group('company', companyId, properties);
};

export const resetPostHogUser = (): void => {
    if (!isPostHogEnabled()) {
        return;
    }

    posthog.reset();
};

export const isFeatureEnabled = (featureFlagKey: string): boolean => {
    if (!isPostHogEnabled()) {
        return false;
    }

    return posthog.isFeatureEnabled(featureFlagKey) === true;
};

export const getFeatureVariant = (featureFlagKey: string): string | boolean | undefined => {
    if (!isPostHogEnabled()) {
        return undefined;
    }

    return posthog.getFeatureFlag(featureFlagKey);
};

export const getFeaturePayload = (featureFlagKey: string): unknown => {
    if (!isPostHogEnabled()) {
        return undefined;
    }

    const result = posthog.getFeatureFlagResult(featureFlagKey);
    return result?.payload;
};

export const getEarlyAccessFeatures = (): Promise<unknown[]> => {
    if (!isPostHogEnabled()) {
        return Promise.resolve([]);
    }

    return new Promise((resolve) => {
        posthog.getEarlyAccessFeatures((features: unknown[]) => {
            resolve(features);
        });
    });
};

export const updateEarlyAccessEnrollment = (
    key: string,
    isEnrolled: boolean,
    stage?: 'concept' | 'alpha' | 'beta' | 'general-availability'
): void => {
    if (!isPostHogEnabled() || !key) {
        return;
    }

    posthog.updateEarlyAccessFeatureEnrollment(key, isEnrolled, stage);
};

export const getActiveMatchingSurveys = (): Promise<unknown[]> => {
    if (!isPostHogEnabled() || shouldDisableSurveys) {
        return Promise.resolve([]);
    }

    return new Promise((resolve) => {
        posthog.getActiveMatchingSurveys((surveys: unknown[]) => {
            resolve(surveys);
        });
    });
};

export const renderPostHogSurvey = (surveyId: string, selector: string): boolean => {
    if (!isPostHogEnabled() || shouldDisableSurveys || !surveyId || !selector) {
        return false;
    }

    const canRenderReason = posthog.canRenderSurvey(surveyId);
    if (canRenderReason !== null) {
        return false;
    }

    posthog.renderSurvey(surveyId, selector);
    return true;
};

export const capturePostHogException = (
    error: unknown,
    properties?: Record<string, unknown>
): void => {
    if (!isPostHogEnabled()) {
        return;
    }

    posthog.captureException(error, properties);
};
